import React, {useCallback, useEffect, useRef, useState} from "react"
import {UploadFile} from "antd/lib/upload/interface"
import useLegendStore from "../../../../store/legendStore"
import {UploadProps} from "antd/lib/upload";
import {ConvertHexToRGBA, ConvertRGBtoHex, randomColor} from "../../../../utils/common";
import {IUploadedLayer} from "../../../../types/legend";
import {ColorResult} from "react-color";
import {isEmpty} from "underscore";
import { useFileStatusPollingQuery, useFileUploadMutation, useGetGeojsonShapeFileQuery } from "../../../../queries/useUploadQuery";

export const useShapefileUpload = () => {
  const [showUploadList, setShowUploadList] = useState(false)
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [selectedLayerId, setSelectedLayerId] = useState<string>('')
  const [currentLayerColor, setCurrentLayerColor] = useState<string>('')
  const filesToUploadRef = useRef<File[]>([])

  const uploadedFeatures = useLegendStore(state => state.uploadedFeatures)
  const setUploadedFeatures = useLegendStore(state => state.setUploadedFeatures)
  const visibleUploads = useLegendStore(state => state.visibleUploads)
  const setVisibleUploads = useLegendStore(state => state.setVisibleUploads)
  const uploadingShapefile = useLegendStore(state => state.uploadingShapefile)
  const setUploadingShapefile = useLegendStore(state => state.setUploadingShapefile)
  const setShowUploadedShapefile = useLegendStore(state => state.setShowUploadedShapefile)

  const [orderId, setOrderId] = useState('')
  const [uploadedFileName, setUploadedFileName] = useState('')
  const [enablePolling, setEnablePolling] = useState(false)

  const uploadFileMutation = useFileUploadMutation()
  const { isSuccess: isSuccessFileStatus } = useFileStatusPollingQuery(orderId, enablePolling)
  const { data: geojsonShapeFile } = useGetGeojsonShapeFileQuery(orderId, uploadedFileName, isSuccessFileStatus)

  useEffect(() => {
    if (geojsonShapeFile?.features) {
      const newLayer = {
        id: uploadedFileName,
        features: geojsonShapeFile.features,
        color: randomColor
      }

      setUploadedFeatures([...uploadedFeatures, newLayer])
      setVisibleUploads([...visibleUploads, uploadedFileName])
      setShowUploadedShapefile(true)
      resetState()
    }else {
      resetState()
    }
  }, [geojsonShapeFile])

  const checkShapefileToUpload = useCallback((files: File[]): boolean => {
    if (files.length !== 3) {
      return false
    }

    const fullNames = files.map(file => file.name.split('.'))
    const names = fullNames.map(fullName => fullName.length === 2 ? fullName[0] : '')

    if (names.length !== 3) {
      return false
    }

    if (names[0] !== names[1] || names[1] !== names[2]) {
      return false
    }

    const extensions = fullNames.map(fullName => fullName[1])
    return (
      extensions.includes('shp') &&
      extensions.includes('shx') &&
      extensions.includes('prj')
    )
  }, [])

  const getFileName = useCallback((files: File[]): string => {
    let name = ''

    if (files.length > 0) {
      const file = files[0]
      const fullName = file.name.split('.')
      if (fullName.length > 0) {
        name = fullName[0]
      }
    }

    const existingNames = uploadedFeatures.map(layer => layer.id)

    if (existingNames.includes(name)) {
      let num = 1
      while (existingNames.some(label => label === `${name} (${num})`)) {
        num++
      }
      return `${name} (${num})`
    }

    return name
  }, [uploadedFeatures])

  const uploadFiles = useCallback(async (file: File): Promise<void> => {
    filesToUploadRef.current.push(file)

    if (filesToUploadRef.current.length > 3) {
      filesToUploadRef.current = filesToUploadRef.current.slice(-3)
    }

    if (!checkShapefileToUpload(filesToUploadRef.current)) {
      setUploadingShapefile(false)
      return
    }

    const name = getFileName(filesToUploadRef.current)
    setUploadingShapefile(true)
    setUploadedFileName(name)
    const filesToProcess = [...filesToUploadRef.current]
    try {
      const orderId = crypto.randomUUID()

      uploadFileMutation.mutate({
        fileType: 'shape',
        fileNum: 3,
        orderId: orderId,
        fileData: filesToProcess,
      },{
        onSuccess: async () => {
          setOrderId(orderId)
          setEnablePolling(true)
        }
      })
    } catch (e) {
      console.error(e)
    }
  }, [
    checkShapefileToUpload,
    getFileName,
    uploadedFeatures,
    visibleUploads,
    setUploadedFeatures,
    setVisibleUploads,
    setShowUploadedShapefile,
    setUploadingShapefile
  ])

  const handleOnUpload = useCallback(() => {
    setShowUploadList(true)
  }, [])

  const onChangeUploadVisibility = useCallback((layerId: string) => {
    setVisibleUploads(visibleUploads.includes(layerId) ? visibleUploads.filter(id => id !== layerId) : [...visibleUploads, layerId])
  }, [visibleUploads, setVisibleUploads])

  const deleteUpload = useCallback((layerId: string) => {
    setUploadedFeatures(
      uploadedFeatures.filter(layer => layer.id !== layerId)
    )
  }, [uploadedFeatures, setUploadedFeatures])

  const onCloseColorPickerDialog = useCallback(() => {
    setSelectedLayerId('')
    setCurrentLayerColor('')
  }, [])

  const applyColor = useCallback((color: ColorResult) => {
    if (isEmpty(selectedLayerId)) return;

    const newColor = ConvertHexToRGBA(color.hex);

    setUploadedFeatures(
      uploadedFeatures.map(layer =>
        layer.id === selectedLayerId
          ? { ...layer, color: newColor }
          : layer
      )
    );
  }, [selectedLayerId, uploadedFeatures, setUploadedFeatures]);

  const changeColor = useCallback((layer: IUploadedLayer) => {
    if (!layer) return;

    setSelectedLayerId(layer.id);
    setCurrentLayerColor(ConvertRGBtoHex(layer.color));
  }, []);

  const resetState = useCallback(() => {
    setShowUploadList(false)
    setUploadedFileName('')
    setFileList([])
    setOrderId('')
    filesToUploadRef.current = []
  }, [])

  const uploadProps: UploadProps = {
    accept: '.shp, .shx, .prj',
    multiple: true,
    maxCount: 3,
    showUploadList,
    beforeUpload: (file) => {
      if (file) {
        uploadFiles(file);
      }
      return false;
    },
    onChange: (info) => {
      setFileList(info.fileList);

      if (info.file.status === 'done' || info.file.status === 'error') {
        setUploadingShapefile(false);
      }
    },
  };

  return {
    showUploadList,
    fileList,
    uploadProps,
    uploadedFeatures,
    visibleUploads,
    uploadingShapefile,
    selectedLayerId,
    currentLayerColor,

    setCurrentLayerColor,
    handleOnUpload,
    onChangeUploadVisibility,
    deleteUpload,
    onCloseColorPickerDialog,
    applyColor,
    changeColor
  };

}
